import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import VoiceLogo from "@/components/VoiceLogo";
import { ArrowLeft } from "lucide-react";

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0f1419', color: '#f5f5f5' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 p-4" style={{ backgroundColor: 'rgba(15, 20, 25, 0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #2d3748' }}>
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-800">
            <ArrowLeft className="h-5 w-5" style={{ color: '#a0aec0' }} />
          </button>
          <div className="flex items-center gap-2">
            <VoiceLogo size={28} />
            <span className="text-lg font-semibold">Voice Capture</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-2">Terms & Conditions</h1>
        <p className="text-sm mb-8" style={{ color: '#718096' }}>Last updated: December 2025</p>

        <div className="space-y-8 text-sm leading-relaxed" style={{ color: '#a0aec0' }}>
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Voice Capture ("the Service"), you agree to be bound by these Terms and Conditions. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Description of Service</h2>
            <p>
              Voice Capture is a voice transcription and text enhancement application that provides:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Voice-to-text transcription</li>
              <li>Grammar and spelling correction</li>
              <li>Tone transformation</li>
              <li>Text translation</li>
              <li>Text-to-speech functionality</li>
              <li>Text history and shortcuts</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. User Accounts</h2>
            <p>
              To use the Service, you must create an account and provide accurate information. You are responsible for:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Privacy & Data Protection</h2>
            <p className="mb-3">
              <strong className="text-white">We take your privacy seriously.</strong> Voice Capture:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-green-400">Does NOT</strong> share your personal information (email, password, or data) with third parties</li>
              <li><strong className="text-green-400">Does NOT</strong> sell your data to advertisers or data brokers</li>
              <li><strong className="text-green-400">Does NOT</strong> store your audio recordings after transcription</li>
              <li>Uses industry-standard encryption to protect your data</li>
              <li>Only retains text history for your convenience, which you can delete at any time</li>
            </ul>
            <p className="mt-3">
              Your transcriptions are processed using secure third-party AI services (OpenAI) solely for the purpose of providing 
              the Service. These services do not use your data for training or other purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Subscription & Billing</h2>
            <p className="mb-3">Voice Capture offers the following plans:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Free Trial:</strong> 10 minutes of transcription, all features included</li>
              <li><strong>Standard ($2.99/month):</strong> 100 minutes transcription, 25 document edits</li>
              <li><strong>Pro ($4.99/month):</strong> Unlimited transcription and editing</li>
            </ul>
            <p className="mt-3">
              Subscriptions automatically renew unless canceled. You may cancel at any time through the app menu 
              or through your app store subscription settings. No refunds are provided for partial months.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Transmit harmful, offensive, or illegal content</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Create multiple accounts to abuse the free trial</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Intellectual Property</h2>
            <p>
              The Service, including its design, features, and content, is owned by Voice Capture and protected by 
              intellectual property laws. You retain ownership of any text you create using the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Service Availability</h2>
            <p>
              We strive to provide reliable service but do not guarantee uninterrupted access. The Service may be 
              temporarily unavailable due to maintenance, updates, or circumstances beyond our control. We are not 
              liable for any losses resulting from service interruptions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Limitation of Liability</h2>
            <p>
              Voice Capture is provided "as is" without warranties of any kind. We are not liable for:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Transcription errors or inaccuracies</li>
              <li>Data loss or corruption</li>
              <li>Indirect, incidental, or consequential damages</li>
              <li>Damages exceeding the amount you paid for the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. Continued use of the Service after changes constitutes 
              acceptance of the new Terms. We will notify users of significant changes via email or in-app notification.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account for violations of these Terms. You may 
              terminate your account at any time by canceling your subscription and contacting support.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Contact Us</h2>
            <p>
              If you have questions about these Terms, please contact us at:
            </p>
            <p className="mt-2">
              <strong className="text-white">Email:</strong>{" "}
              <a href="mailto:support@voicecapture.net" className="text-purple-400 hover:underline">
                support@voicecapture.net
              </a>
            </p>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-12 pt-8" style={{ borderTop: '1px solid #2d3748' }}>
          <Button onClick={() => navigate('/')} className="rounded-full px-6" style={{ backgroundColor: '#6366f1' }}>
            Back to Home
          </Button>
        </div>
      </main>
    </div>
  );
}
